import React, { useState, useRef } from 'react';
import { useConfirm } from '../contexts/ConfirmContext';
import { uploadToImgbb } from '../utils/imgbb';

const PRIORITY_OPTIONS = [
  { value: 0, label: '通常',    desc: '通常のマッチング' },
  { value: 1, label: '優先保証', desc: '第1希望のゲストと必ずマッチ' },
  { value: 2, label: '優先 2',  desc: '競合時に優先される' },
  { value: 3, label: '優先 3',  desc: '競合時に優先される' },
];

const CastCard = ({
  castId, cast, onRemove, onToggleStatus, onUpdateTags, onUpdatePriority,
  onOpenPicker, showPicker, confirm, sessions, sessionAttendanceToday,
  onSetSessionAttendance, onUpdate, existingCasts,
  showLeader, onToggleLeader, instanceCount, onSetPinnedInstance,
}) => {
  const isLeader = !!cast.is_instance_leader;
  const [hovAttend,  setHovAttend]  = useState(false);
  const [hovPicker,  setHovPicker]  = useState(false);
  const [hovTag,     setHovTag]     = useState(false);
  const [hovDel,     setHovDel]     = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [editing,    setEditing]    = useState(false);
  const [editName,    setEditName]    = useState('');
  const [editProfile, setEditProfile] = useState('');
  const [editXUrl,    setEditXUrl]    = useState('');
  const [editImage,   setEditImage]   = useState('');
  const [imgStatus,   setImgStatus]   = useState('idle');
  const [imgErr,      setImgErr]      = useState('');
  const fileInputRef = useRef(null);

  const isWorking = sessions
    ? Object.values(sessionAttendanceToday || {}).some(s => s === 'working')
    : cast.status === 'working';
  const priority = cast.priority || 0;
  const picks    = cast.draft_picks || [];

  const startEdit = () => {
    setEditName(cast.vrchat_name || '');
    setEditProfile(cast.vrchat_profile || '');
    setEditXUrl(cast.x_url || '');
    setEditImage(cast.image_url || '');
    setImgStatus('idle');
    setImgErr('');
    setEditing(true);
  };

  const isDupName = (name) =>
    Object.entries(existingCasts || {}).some(
      ([id, c]) => id !== castId && (c.vrchat_name || c.name || '').trim().toLowerCase() === name.trim().toLowerCase()
    );

  const handleSave = () => {
    if (!editName.trim() || isDupName(editName) || imgStatus === 'uploading') return;
    onUpdate(castId, {
      vrchat_name:    editName.trim(),
      vrchat_profile: editProfile.trim() || null,
      x_url:          editXUrl.trim()    || null,
      image_url:      editImage          || null,
    });
    setEditing(false);
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setImgErr('画像ファイルを選択してください'); return; }
    if (file.size > 32 * 1024 * 1024) { setImgErr('32MB以下の画像を選択してください'); return; }
    setImgStatus('uploading');
    setImgErr('');
    try {
      const url = await uploadToImgbb(file);
      setEditImage(url);
      setImgStatus('done');
    } catch (err) {
      setImgErr(err.message || 'アップロードに失敗しました');
      setImgStatus('error');
    }
    e.target.value = '';
  };

  const dupName = editName.trim() && isDupName(editName);

  return (
    <div style={{
      borderRadius: '12px',
      border: '1px solid #e8edf5',
      background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      borderLeft: `3px solid ${isWorking ? '#6c63ff' : '#e2e8f0'}`,
      overflow: 'hidden',
    }}>
      {/* ── Main row ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px' }}>

        {/* Avatar */}
        <div style={{
          width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
          background: isWorking ? '#6c63ff' : '#e2e8f0',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: '800', fontSize: '16px',
          transition: 'background 0.2s', overflow: 'hidden',
        }}>
          {cast.image_url
            ? <img src={cast.image_url} alt={cast.vrchat_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : (cast.vrchat_name || cast.name || '?').charAt(0).toUpperCase()
          }
        </div>

        {/* Name + tags */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>
            {cast.vrchat_name || cast.name}
          </div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {showLeader && isLeader && (
              <span style={{ background: '#fef9c3', color: '#a16207', borderRadius: '4px', padding: '1px 6px', fontSize: '10px', fontWeight: '800' }}>
                👑 リーダー
              </span>
            )}
            {showLeader && cast.pinned_instance != null && (!instanceCount || cast.pinned_instance <= instanceCount) && (
              <span style={{ background: '#dbeafe', color: '#1d4ed8', borderRadius: '4px', padding: '1px 6px', fontSize: '10px', fontWeight: '800' }}>
                📍 #{cast.pinned_instance}
              </span>
            )}
            {priority >= 1 && (
              <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: '4px', padding: '1px 6px', fontSize: '10px', fontWeight: '700' }}>
                ⭐ {PRIORITY_OPTIONS.find(o => o.value === priority)?.label}
              </span>
            )}
            {picks.length > 0 && (
              <span style={{ background: '#ede9fe', color: '#5b21b6', borderRadius: '4px', padding: '1px 6px', fontSize: '10px', fontWeight: '700' }}>
                🎯 指名 {picks.length}件
              </span>
            )}
            {(cast.tags || []).map(tag => (
              <span key={tag} style={{ background: '#f1f5f9', color: '#475569', borderRadius: '4px', padding: '1px 6px', fontSize: '10px', fontWeight: '600' }}>
                #{tag}
              </span>
            ))}
          </div>
        </div>

        {/* Attendance toggle */}
        {sessions ? (
          <div style={{ display: 'flex', gap: 4, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 220 }}>
            {Array.from({ length: sessions }, (_, i) => {
              const ses = i + 1;
              const sesWorking = sessionAttendanceToday?.[ses] === 'working';
              return (
                <button key={ses} onClick={() => onSetSessionAttendance(ses, sesWorking ? null : 'working')}
                  style={{
                    padding: '7px 10px', borderRadius: 7, border: 'none', cursor: 'pointer',
                    fontWeight: 800, fontSize: 12, whiteSpace: 'nowrap', transition: 'all 0.15s',
                    background: sesWorking ? '#6c63ff' : '#f1f5f9',
                    color: sesWorking ? '#fff' : '#94a3b8',
                  }}
                >
                  第{ses}部{sesWorking ? ' ✓' : ''}
                </button>
              );
            })}
          </div>
        ) : (
          <button
            onMouseEnter={() => setHovAttend(true)}
            onMouseLeave={() => setHovAttend(false)}
            onClick={() => onToggleStatus(castId, cast.status)}
            style={{
              flexShrink: 0, padding: '9px 16px', borderRadius: '8px', border: 'none',
              cursor: 'pointer', fontWeight: '800', fontSize: '13px', transition: 'all 0.15s',
              ...(isWorking
                ? { background: hovAttend ? '#fef2f2' : '#ecfdf5', color: hovAttend ? '#ef4444' : '#10b981' }
                : { background: hovAttend ? '#f5f3ff' : '#f8fafc', color: hovAttend ? '#6c63ff' : '#94a3b8' }
              ),
            }}
          >
            {isWorking ? (hovAttend ? '退勤する' : '出勤中 ✓') : (hovAttend ? '出勤する' : '未出勤')}
          </button>
        )}
      </div>

      {/* ── Sub actions row ───────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '8px 16px', borderTop: '1px solid #f1f5f9',
        background: '#fafbfc', flexWrap: 'wrap',
      }}>

        {/* 指名設定 */}
        {showPicker && (
          <button
            onMouseEnter={() => setHovPicker(true)}
            onMouseLeave={() => setHovPicker(false)}
            onClick={() => onOpenPicker(castId)}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
              border: `1px solid ${hovPicker ? '#c4b5fd' : '#e2e8f0'}`, borderRadius: '6px',
              background: hovPicker ? '#f5f3ff' : '#fff', color: hovPicker ? '#6c63ff' : '#475569',
              fontSize: '12px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            🎯 指名設定
            {picks.length > 0 && (
              <span style={{ background: '#6c63ff', color: '#fff', borderRadius: '999px', padding: '0 5px', fontSize: '10px', fontWeight: '800' }}>
                {picks.length}
              </span>
            )}
          </button>
        )}

        {/* タグ編集 */}
        <button
          onMouseEnter={() => setHovTag(true)}
          onMouseLeave={() => setHovTag(false)}
          onClick={() => {
            const cur = (cast.tags || []).join(',');
            const res = window.prompt('タグをカンマ区切りで入力', cur);
            if (res === null) return;
            onUpdateTags(castId, res.split(',').map(t => t.trim()).filter(Boolean));
          }}
          style={{
            display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
            border: `1px solid ${hovTag ? '#d1d5db' : '#e2e8f0'}`, borderRadius: '6px',
            background: hovTag ? '#f9fafb' : '#fff', color: '#475569',
            fontSize: '12px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s',
          }}
        >
          🏷 タグ
        </button>

        {/* インスタンスリーダー（cast_instance のみ） */}
        {showLeader && (
          <button
            onClick={() => onToggleLeader(castId, !isLeader)}
            title="このキャストをインスタンスリーダーに設定します（各インスタンスに必ず1人配置されます）"
            style={{
              display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
              border: `1px solid ${isLeader ? '#fde047' : '#e2e8f0'}`, borderRadius: '6px',
              background: isLeader ? '#fef9c3' : '#fff', color: isLeader ? '#a16207' : '#475569',
              fontSize: '12px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            👑 リーダー{isLeader ? ' ✓' : ''}
          </button>
        )}

        {/* インスタンス固定（cast_instance のみ） */}
        {showLeader && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', whiteSpace: 'nowrap' }}>📍 インスタンス先</span>
            {instanceCount ? (
              <select
                value={
                  cast.pinned_instance != null && cast.pinned_instance >= 1 && cast.pinned_instance <= instanceCount
                    ? cast.pinned_instance
                    : ''
                }
                onChange={(e) => onSetPinnedInstance(castId, e.target.value === '' ? null : parseInt(e.target.value, 10))}
                title="このキャストを割り当てるインスタンスを固定します（未設定はランダム）"
                style={{
                  padding: '5px 8px', border: `1px solid ${cast.pinned_instance != null && cast.pinned_instance <= instanceCount ? '#93c5fd' : '#e2e8f0'}`,
                  borderRadius: '6px',
                  background: cast.pinned_instance != null && cast.pinned_instance <= instanceCount ? '#eff6ff' : '#fff',
                  color: cast.pinned_instance != null && cast.pinned_instance <= instanceCount ? '#1d4ed8' : '#475569',
                  fontSize: '12px', fontWeight: '700', cursor: 'pointer', outline: 'none',
                }}
              >
                <option value="">ランダム</option>
                {Array.from({ length: instanceCount }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>#{n}</option>
                ))}
              </select>
            ) : (
              <input
                type="number"
                min="1"
                value={cast.pinned_instance ?? ''}
                onChange={(e) => onSetPinnedInstance(castId, e.target.value === '' ? null : parseInt(e.target.value, 10))}
                placeholder="―"
                title="このキャストを割り当てるインスタンス番号を固定します（空欄はランダム）"
                style={{
                  width: '64px', padding: '5px 8px',
                  border: `1px solid ${cast.pinned_instance != null ? '#93c5fd' : '#e2e8f0'}`,
                  borderRadius: '6px',
                  background: cast.pinned_instance != null ? '#eff6ff' : '#fff',
                  color: cast.pinned_instance != null ? '#1d4ed8' : '#475569',
                  fontSize: '12px', fontWeight: '700', outline: 'none',
                }}
              />
            )}
          </div>
        )}

        {/* 優先度 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginLeft: '2px' }}>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', whiteSpace: 'nowrap' }}>優先度</span>
          <select
            value={priority}
            onChange={(e) => onUpdatePriority(castId, parseInt(e.target.value, 10))}
            title="優先保証に設定すると、このキャストの第1希望ゲストと必ずマッチします"
            style={{
              padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '6px',
              background: priority >= 1 ? '#fffbeb' : '#fff',
              color: priority >= 1 ? '#92400e' : '#475569',
              fontSize: '12px', fontWeight: '700', cursor: 'pointer', outline: 'none',
            }}
          >
            {PRIORITY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* 詳細/編集 */}
        <button
          onClick={() => { setShowDetail(v => !v); if (showDetail) setEditing(false); }}
          style={{
            padding: '6px 12px',
            border: `1px solid ${showDetail ? '#6c63ff' : '#e2e8f0'}`, borderRadius: '6px',
            background: showDetail ? '#f5f3ff' : '#fff',
            color: showDetail ? '#6c63ff' : '#475569',
            fontSize: '12px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s',
          }}
        >
          {showDetail ? '▲ 詳細を閉じる' : '▼ 詳細'}
        </button>

        {/* 削除 */}
        <div style={{ marginLeft: 'auto' }}>
          <button
            onMouseEnter={() => setHovDel(true)}
            onMouseLeave={() => setHovDel(false)}
            onClick={async () => {
              if (await confirm({ message: `${cast.vrchat_name || cast.name}を削除しますか？`, type: 'danger' })) onRemove(castId);
            }}
            style={{
              padding: '6px 12px',
              border: `1px solid ${hovDel ? '#fca5a5' : '#e2e8f0'}`, borderRadius: '6px',
              background: hovDel ? '#fef2f2' : '#fff',
              color: hovDel ? '#ef4444' : '#94a3b8',
              fontSize: '12px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            削除
          </button>
        </div>
      </div>

      {/* ── Detail panel ─────────────────────────────────────────────── */}
      {showDetail && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', background: '#f8fafc' }}>
          {!editing ? (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {cast.vrchat_profile && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', minWidth: 28 }}>VRC</span>
                    <a href={cast.vrchat_profile} target="_blank" rel="noreferrer"
                      style={{ fontSize: '13px', color: '#6c63ff', fontWeight: '600', textDecoration: 'none', wordBreak: 'break-all' }}
                      onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                      onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                    >{cast.vrchat_profile}</a>
                  </div>
                )}
                {cast.x_url && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', minWidth: 28 }}>𝕏</span>
                    <a href={cast.x_url} target="_blank" rel="noreferrer"
                      style={{ fontSize: '13px', color: '#6c63ff', fontWeight: '600', textDecoration: 'none', wordBreak: 'break-all' }}
                      onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                      onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                    >{cast.x_url}</a>
                  </div>
                )}
                {!cast.vrchat_profile && !cast.x_url && !cast.image_url && (
                  <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>プロフィール情報なし</p>
                )}
              </div>
              <button onClick={startEdit}
                style={{
                  flexShrink: 0, padding: '6px 14px', border: '1.5px solid #6c63ff', borderRadius: '6px',
                  background: '#fff', color: '#6c63ff', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
                }}
              >
                編集
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageSelect} />

              {/* 画像アップロード */}
              <div>
                <label style={editLabel}>プロフィール画像 <span style={optionalStyle}>任意</span></label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      width: 56, height: 56, borderRadius: '50%', cursor: 'pointer',
                      border: '2px dashed #c4b5fd', overflow: 'hidden', flexShrink: 0,
                      background: editImage ? 'transparent' : 'linear-gradient(135deg, #6c63ff, #8b5cf6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {editImage
                      ? <img src={editImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ fontSize: 20, color: '#fff' }}>📷</span>
                    }
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button type="button" onClick={() => fileInputRef.current?.click()}
                      disabled={imgStatus === 'uploading'}
                      style={{ ...subBtn, opacity: imgStatus === 'uploading' ? 0.6 : 1 }}
                    >
                      {imgStatus === 'uploading' ? 'アップロード中...' : editImage ? '画像を変更' : '画像を選択'}
                    </button>
                    {editImage && (
                      <button type="button" onClick={() => { setEditImage(''); setImgStatus('idle'); }}
                        style={{ ...subBtn, color: '#ef4444', borderColor: '#fca5a5' }}>
                        画像を削除
                      </button>
                    )}
                    {imgErr && <span style={{ fontSize: 11, color: '#dc2626' }}>{imgErr}</span>}
                  </div>
                </div>
              </div>

              {/* VRChat名 */}
              <div>
                <label style={editLabel}>VRChat名 <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  style={{ ...editInput, borderColor: dupName ? '#ef4444' : '#e2e8f0' }}
                />
                {dupName && <span style={{ fontSize: 11, color: '#dc2626' }}>⚠️ 同じVRChat名がすでに登録されています</span>}
              </div>

              {/* VRChatプロフィールURL */}
              <div>
                <label style={editLabel}>VRChatプロフィールURL <span style={optionalStyle}>任意</span></label>
                <input
                  value={editProfile}
                  onChange={e => setEditProfile(e.target.value)}
                  placeholder="https://vrchat.com/home/user/usr_..."
                  style={editInput}
                />
              </div>

              {/* X URL */}
              <div>
                <label style={editLabel}>X (Twitter) URL <span style={optionalStyle}>任意</span></label>
                <input
                  value={editXUrl}
                  onChange={e => setEditXUrl(e.target.value)}
                  placeholder="https://x.com/yourname"
                  style={editInput}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={handleSave}
                  disabled={!editName.trim() || !!dupName || imgStatus === 'uploading'}
                  style={{
                    flex: 1, padding: '9px', borderRadius: '8px', border: 'none',
                    background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)',
                    color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                    opacity: (!editName.trim() || dupName || imgStatus === 'uploading') ? 0.5 : 1,
                  }}
                >
                  保存
                </button>
                <button onClick={() => setEditing(false)}
                  style={{
                    flex: 1, padding: '9px', borderRadius: '8px', border: '1px solid #e2e8f0',
                    background: '#fff', color: '#666', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                  }}
                >
                  キャンセル
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const editLabel   = { display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 };
const optionalStyle = { marginLeft: 6, fontSize: 11, color: '#94a3b8', fontWeight: 400 };
const editInput   = { width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' };
const subBtn      = { padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: '12px', fontWeight: '700', cursor: 'pointer' };

const CastList = ({
  casts, onRemove, onUpdateTags, onUpdatePriority, onOpenPicker,
  onToggleStatus, onUpdate, matchingMode, sessions, sessionAttendance, today, onSetSessionAttendance,
  showLeader, onToggleLeader, instanceCount, onSetPinnedInstance,
}) => {
  const confirm = useConfirm();

  if (Object.keys(casts).length === 0) return null;

  const mode       = matchingMode || 'cast_select';
  const showPicker = mode === 'cast_select' || mode === 'both_select';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
      {Object.entries(casts).map(([castId, cast]) => (
        <CastCard
          key={castId}
          castId={castId}
          cast={cast}
          onRemove={onRemove}
          onToggleStatus={onToggleStatus}
          onUpdateTags={onUpdateTags}
          onUpdatePriority={onUpdatePriority}
          onOpenPicker={onOpenPicker}
          showPicker={showPicker}
          confirm={confirm}
          sessions={sessions}
          sessionAttendanceToday={sessionAttendance?.[castId]?.[today]}
          onSetSessionAttendance={(ses, status) => onSetSessionAttendance(castId, ses, status)}
          onUpdate={onUpdate}
          existingCasts={casts}
          showLeader={showLeader}
          onToggleLeader={onToggleLeader}
          instanceCount={instanceCount}
          onSetPinnedInstance={onSetPinnedInstance}
        />
      ))}
    </div>
  );
};

export default CastList;
